import React from "react"
import type { FieldPath } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { usePersonNameOrder } from "@/formatters"
import type { UserRoleType } from "@/models/user-role"
import { FormSelectField } from "@/nzds/forms/form-select-field/form-select-field"
import { FormTextField } from "@/nzds/forms/form-text-field/form-text-field"

import { useRoleSelectProps } from "./use-role-select-props"

interface UserIdentityFormValues {
  email: string
  firstName: string
  lastName: string
  role: UserRoleType
}

type UserNameFieldName = FieldPath<
  Pick<UserIdentityFormValues, "firstName" | "lastName">
>

interface UserIdentityFieldsProps {
  disabled?: boolean
}

export const UserIdentityFields: React.FC<UserIdentityFieldsProps> = ({
  disabled = false,
}) => {
  const { t } = useTranslation("common")
  const personNameOrder = usePersonNameOrder()
  const { roleOptions, renderRoleOption } = useRoleSelectProps()

  const nameFieldNames: UserNameFieldName[] =
    personNameOrder === "family_first"
      ? ["lastName", "firstName"]
      : ["firstName", "lastName"]

  const nameLabelKeys: Record<UserNameFieldName, string> = {
    firstName: "fields.firstName.title",
    lastName: "fields.lastName.title",
  }

  return (
    <>
      {nameFieldNames.map((name) => (
        <FormTextField<UserIdentityFormValues>
          disabled={disabled}
          key={name}
          label={t(nameLabelKeys[name])}
          name={name}
          required
          variant="stacked"
        />
      ))}
      <FormTextField<UserIdentityFormValues>
        disabled={disabled}
        label={t("fields.email.title")}
        name="email"
        required
        type="email"
        variant="stacked"
      />
      <FormSelectField<UserIdentityFormValues>
        disabled={disabled}
        label={t("fields.role.title")}
        name="role"
        options={roleOptions}
        renderOption={renderRoleOption}
        required
        variant="stacked"
      />
    </>
  )
}
